import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeSlash, ArrowRight, Basketball, Megaphone, EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import GoogleAuthButton from '../components/GoogleAuthButton';
import { LOGO_URL } from '../lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const [step, setStep] = useState(1); // 1 = pick role, 2 = form, 3 = check-your-email
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'player', city: '', phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(true);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState('');
  const { register } = useAuth();

  const handleRoleSelect = (role) => {
    setFormData({ ...formData, role });
    setStep(2);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await register(formData.email, formData.password, formData.name, formData.role, formData.city, formData.phone);
    setLoading(false);
    if (result.success) {
      setVerificationEmailSent(result.verificationEmailSent !== false);
      setStep(3);
    } else {
      setError(result.error);
    }
  };

  const handleResend = async () => {
    setResendBusy(true);
    setResendNote('');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
      setResendNote('Verification email resent. Please check your inbox.');
    } catch {
      setResendNote('Could not resend right now. Please try again in a moment.');
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <>
      <SEO title="Create Account" description="Join Beyond Ball India. Register as a player to discover tournaments, or as an organizer to host basketball tournaments across India." path="/register" />
      <div className="min-h-screen flex items-center justify-center px-4 py-24 court-pattern">
        <div className="max-w-md w-full">
          <div className="text-center mb-8 animate-fade-up">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Beyond Ball India" className="h-14 w-14 rounded-full object-contain mx-auto" />
            </Link>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-2" data-testid="register-title">
              {step === 1 && 'Join Beyond Ball India'}
              {step === 2 && `Create ${formData.role} account`}
              {step === 3 && 'Check your email'}
            </h1>
            <p className="text-gray-600">
              {step === 1 && 'Choose how you want to use the platform'}
              {step === 2 && "Tell us a bit about yourself"}
              {step === 3 && "We've sent you a verification link to get started"}
            </p>
          </div>

          {step === 3 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm animate-fade-up text-center" data-testid="register-check-email">
              <div className={`w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center ${verificationEmailSent ? 'bg-orange-50' : 'bg-amber-50'}`}>
                {verificationEmailSent
                  ? <EnvelopeSimple size={28} weight="bold" className="text-[#F97316]" />
                  : <EnvelopeSimple size={28} weight="bold" className="text-amber-600" />}
              </div>
              {verificationEmailSent ? (
                <>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Verification link sent</h2>
                  <p className="text-sm text-gray-600 mb-1">We&apos;ve emailed a verification link to:</p>
                  <p className="text-sm font-semibold text-gray-900 mb-5">{formData.email}</p>
                  <p className="text-sm text-gray-500 mb-6">
                    Click the link in that email to activate your account. The link expires in 7 days.
                    Once verified, you can log in.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Account created</h2>
                  <p className="text-sm text-gray-600 mb-1">We could not send the verification email right now.</p>
                  <p className="text-sm font-semibold text-gray-900 mb-5">{formData.email}</p>
                  <p className="text-sm text-gray-500 mb-6">
                    Please use the button below to resend the verification email. If it still doesn&apos;t arrive within a few minutes,
                    contact <a href="mailto:beyondballindia@gmail.com" className="text-[#F97316] font-semibold">beyondballindia@gmail.com</a>.
                  </p>
                </>
              )}

              {resendNote && (
                <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-2.5 mb-4 text-xs rounded-lg" data-testid="register-resend-note">
                  {resendNote}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <button
                  onClick={handleResend}
                  disabled={resendBusy}
                  className="w-full bg-gray-900 hover:bg-black text-white px-5 py-3 rounded-xl text-sm font-semibold disabled:opacity-50"
                  data-testid="register-resend-btn"
                >
                  {resendBusy ? 'Sending...' : 'Resend verification email'}
                </button>
                <Link
                  to="/login"
                  className="w-full block text-center bg-white border border-gray-200 hover:border-gray-400 text-gray-900 px-5 py-3 rounded-xl text-sm font-semibold"
                  data-testid="register-go-login-btn"
                >
                  Go to Login
                </Link>
              </div>

              <p className="text-xs text-gray-400 mt-6 flex items-center justify-center gap-1">
                <CheckCircle size={12} weight="bold" /> Tip: also check your Spam / Promotions folder.
              </p>
            </div>
          ) : step === 1 ? (
            <div className="space-y-3 animate-fade-up delay-100">
              <GoogleAuthButton label="Sign up with Google" testId="register-google-btn" />
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">or with email</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <button
                onClick={() => handleRoleSelect('player')}
                className="w-full bg-white border-2 border-gray-200 hover:border-[#F97316] rounded-2xl p-5 text-left transition-all card-lift group"
                data-testid="role-player-btn"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-50 group-hover:bg-[#F97316] flex items-center justify-center transition-colors">
                    <Basketball size={24} weight="bold" className="text-[#F97316] group-hover:text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 mb-0.5">I&apos;m a Player</div>
                    <div className="text-sm text-gray-500">Discover & register for tournaments</div>
                  </div>
                  <ArrowRight size={20} weight="bold" className="text-gray-400 group-hover:text-[#F97316] group-hover:translate-x-1 transition-all" />
                </div>
              </button>

              <button
                onClick={() => handleRoleSelect('organizer')}
                className="w-full bg-white border-2 border-gray-200 hover:border-gray-900 rounded-2xl p-5 text-left transition-all card-lift group"
                data-testid="role-organizer-btn"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 group-hover:bg-gray-900 flex items-center justify-center transition-colors">
                    <Megaphone size={24} weight="bold" className="text-gray-700 group-hover:text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 mb-0.5">I&apos;m an Organizer</div>
                    <div className="text-sm text-gray-500">Host & manage tournaments</div>
                  </div>
                  <ArrowRight size={20} weight="bold" className="text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all" />
                </div>
              </button>

              <div className="text-center text-sm pt-4">
                <span className="text-gray-600">Already have an account? </span>
                <Link to="/login" className="text-[#F97316] hover:text-orange-700 font-semibold" data-testid="login-link">Sign in</Link>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm animate-fade-up">
              {error && (
                <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 mb-6 text-sm rounded-xl" data-testid="register-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required data-testid="register-name-input"
                    className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-3 text-sm outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="register-email-input"
                    className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-3 text-sm outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password *</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} required data-testid="register-password-input"
                      className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-3 pr-12 text-sm outline-none transition-all" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 p-1" data-testid="toggle-password-visibility">
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">City</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} data-testid="register-city-input"
                      className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-3 text-sm outline-none transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} data-testid="register-phone-input"
                      className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-3 text-sm outline-none transition-all" />
                  </div>
                </div>

                <select name="role" value={formData.role} onChange={handleChange} required data-testid="register-role-select" className="hidden">
                  <option value="player">Player</option>
                  <option value="organizer">Organizer</option>
                </select>

                <button type="submit" disabled={loading} className="btn-primary w-full px-5 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="register-submit-btn">
                  {loading ? 'Creating account...' : 'Create Account'}
                  {!loading && <ArrowRight size={16} weight="bold" />}
                </button>

                <button type="button" onClick={() => setStep(1)} className="w-full text-center text-sm text-gray-500 hover:text-gray-700">
                  ← Change account type
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Register;
